import { Intent } from "@blueprintjs/core";
import { PieChart } from "components/charts";
import { FormTextInput } from "components/form-fields";
import { ShowHide } from "components/layout";
import { ListingTable } from "components/listing";
import { NotificationToaster } from "components/notifications";
import { useDebounce } from "hooks/useDebounce";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState, useMemo } from "react";
import { AccountService } from "services";
import { DocumentsService } from "services/DocumentsService";
import { parseBytesTo } from "utils/parseBytesTo";

export function LocationStorage({
    locationStorage,
    otherLocationStorage,
    loading = false,
    accountId = null
}) {
    const defaultRecordSize = 25;
    const debounceTimeout = 750;

    const [isLoading, setIsLoading] = useState(true);

    const tableHeaders = [
        "Location",
        "Documents",
        "Total Size",
        "% of Total"
    ];

    const sortableHeaders = [
        { headerName: "Location", sortName: "LOCATIONNAME" },
        { headerName: "Documents", sortName: "NUMBEROFDOCUMENTS" },
        { headerName: "Total Size", sortName: "TOTALSIZE" },
        { headerName: "% of Total", sortName: "PERCENTAGEOFTOTAL" }
    ];

    const [sortedDir, setSortedDir] = useState("D");
    const [sortedBy, setSortedBy] = useState(sortableHeaders.find(s => s.headerName === "Total Size"));

    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [tableRows, setTableRows] = useState([]);

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentPageNumber = useRef();
    const currentSearchTerm = useRef();

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function fetchTableData(forceRefresh) {
        if (
            currentTableSortDir.current === sortedDir &&
            currentPageNumber.current === pageNumber &&
            currentTableSortBy.current === sortedBy &&
            currentSearchTerm.current === debouncedSearchTerm &&
            !forceRefresh
        ) {
            //If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
            return;
        }

        currentTableSortBy.current = sortedBy;
        currentPageNumber.current = pageNumber;
        currentTableSortDir.current = sortedDir;
        currentSearchTerm.current = debouncedSearchTerm;

        setIsLoading(true);

        const searchRequest = {
            requestCount: defaultRecordSize,
            pageNumber: pageNumber,
            sortField: sortedBy.sortName,
            sortDirection: sortedDir,
            filters: [],
            searchTerm: debouncedSearchTerm
        };

        const promiseRequest = accountId
            ? AccountService.getLocationStorageBreakdown(accountId, searchRequest)
            : DocumentsService.getLocationStorageBreakdown(searchRequest);

        promiseRequest.then(
            (r) => {
                let data = [];

                if (r?.data != null) {
                    data = r.data.map((d) => {
                        return [
                            d.locationName,
                            d.numberOfDocuments,
                            parseBytesTo(d.totalSize).auto(),
                            d.percentageOfTotal + '%'
                        ];
                    });
                }

                setTableRows(data);
                setTotalRecords(r.totalCount);
                setIsLoading(false);
            },
            () => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not retrieve location storage breakdown, please try again"
                );
            }
        );
    }

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        sortedBy,
        sortedDir,
        debouncedSearchTerm
    ]);
    
    const storagePerAreaChartData = useMemo(() => {
        if (loading || !locationStorage) {
            return [];
        }

        const chartData = locationStorage
            .filter(location => location.totalSize > 0)
            .map(location => ({
                id: location.locationId,
                label: location.locationName,
                value: location.percentageOfTotal
            }));

        if (otherLocationStorage) {
            chartData.push({
                id: -1,
                label: `Other (${otherLocationStorage.totalNumberOfOtherLocations} Location${otherLocationStorage.totalNumberOfOtherLocations !== 1 ? 's' : ''})`,
                value: otherLocationStorage.percentageOfTotal,
                color: '#E0E0E0'
            });
        }
        return chartData;
    }, [locationStorage, otherLocationStorage, loading]);

    return (
        <>
            <div className='spacer-top'>
                <PieChart title="Storage by Location" items={storagePerAreaChartData} noDataMessage="No documents have been uploaded." />
            </div>
            <ShowHide
                evaluator={!!storagePerAreaChartData.length}
                show={<div className="spacer-top">
                    <h3>Locations</h3>
                    <div className="inline-items">
                        <div className="inline-item fill">
                            <div className="form-field-full">
                                <FormTextInput
                                    placeholder="Search Locations"
                                    onChange={(search) => setSearchTerm(search.target.value)}
                                    value={searchTerm}
                                    large
                                    disabled={isLoading}
                                    icon="search"
                                    id="document-location-search-field"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="spacer-top-small">
                        <ListingTable
                            showPerPage={false}
                            pageSize={defaultRecordSize}
                            headers={tableHeaders}
                            data={tableRows}
                            loadingData={isLoading}
                            onPagingChange={(newPageNumber) => setPageNumber(newPageNumber)}
                            totalRecordCount={totalRecords}
                            sortable
                            sortedBy={sortedBy}
                            sortedDir={sortedDir}
                            sortableHeaders={sortableHeaders}
                            onSort={onTableSort}
                        />
                    </div>
                </div>}
            />
        </>
    )
}

LocationStorage.propTypes = {
    locationStorage: PropTypes.array.isRequired,
    otherLocationStorage: PropTypes.object.isRequired,
    loading: PropTypes.bool,
    accountId: PropTypes.number
}